import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  ArrowLeft,
  Mail,
  Sparkles,
  Workflow,
  Target,
  Compass,
  Bot,
  Zap,
  Building2,
  DollarSign,
  Clock,
  TrendingUp,
  ShieldAlert,
  Map as MapIcon,
  Crown,
  Users,
  Award,
  HelpCircle,
} from "lucide-react";
import { PageShell } from "./publicShared";
import { FAQ_ENTRIES, relatedFaqs } from "./faqEntries";

const ICON_MAP = {
  Workflow, Target, Compass, Bot, Zap, Building2, DollarSign, Clock,
  TrendingUp, ShieldAlert, Map: MapIcon, Crown, Users, Sparkles, Award,
};

/* ── JSON-LD for AEO ── */
const StructuredData = ({ entry, canonical }) => {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": entry.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": entry.answer,
        },
      },
    ],
    "url": canonical,
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

export const FAQArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const entry = FAQ_ENTRIES.find((e) => e.slug === slug);

  // Set <title> + meta description for AEO
  useEffect(() => {
    if (!entry) return;
    const prevTitle = document.title;
    document.title = `${entry.question} | 589 Labs`;
    let meta = document.querySelector('meta[name="description"]');
    let created = false;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
      created = true;
    }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute("content", entry.short);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [entry]);

  if (!entry) {
    return (
      <PageShell>
        <section className="bg-white py-24" data-testid="faq-article-not-found">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 text-center">
            <HelpCircle className="mx-auto h-10 w-10 text-[#9ca3af] mb-5" />
            <h1 className="text-[28px] font-bold text-[#111827] tracking-tight">
              We couldn't find that question.
            </h1>
            <p className="mt-3 text-[15px] text-[#4b5563]">
              It may have moved. Browse the full FAQ to find what you're looking for.
            </p>
            <Button
              onClick={() => navigate("/faq")}
              className="mt-7 h-12 rounded-xl bg-[#1a56db] text-white px-6 text-[14.5px] font-semibold hover:bg-[#143faa]"
            >
              Browse all FAQs
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </section>
      </PageShell>
    );
  }

  const Icon = ICON_MAP[entry.icon] || HelpCircle;
  const related = relatedFaqs(entry.slug, 3);
  const canonical = typeof window !== "undefined"
    ? `${window.location.origin}/faq/${entry.slug}`
    : `https://589labs.ai/faq/${entry.slug}`;

  return (
    <PageShell>
      <StructuredData entry={entry} canonical={canonical} />

      {/* ── HERO / ANSWER ── */}
      <section
        className="bg-white py-16 lg:py-20"
        data-testid={`faq-article-${entry.slug}`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          {/* Breadcrumb */}
          <nav className="mb-7 text-[13px] text-[#6b7280]" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#1a56db]">Home</Link>
            <span className="mx-2 text-[#cbd5e1]">/</span>
            <Link to="/faq" className="hover:text-[#1a56db]">FAQ</Link>
            <span className="mx-2 text-[#cbd5e1]">/</span>
            <span className="text-[#111827] font-medium">{entry.question}</span>
          </nav>

          <div className="grid lg:grid-cols-[1.6fr_1fr] gap-10 lg:gap-16 items-start">
            <article>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-11 w-11 rounded-xl bg-[#e8eefb] text-[#1a56db] flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="text-[12px] font-bold tracking-[0.16em] uppercase text-[#1a56db]">
                  AI Strategy FAQ
                </div>
              </div>

              <h1
                className="text-[34px] sm:text-[42px] lg:text-[48px] font-bold text-[#111827] leading-[1.1] tracking-tight"
                style={{ letterSpacing: "-0.5px" }}
                data-testid="faq-article-headline"
              >
                {entry.question}
              </h1>

              {/* Direct answer card — the AEO-quotable block */}
              <div
                className="mt-9 rounded-2xl border-2 border-[#1a56db] bg-[#f8fbff] p-7 sm:p-8"
                data-testid="faq-article-answer"
              >
                <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#1a56db] mb-3">
                  Direct answer
                </div>
                <p className="text-[17px] sm:text-[18px] text-[#111827] leading-[1.7] font-medium" style={{ letterSpacing: "-0.005em" }}>
                  {entry.answer}
                </p>
              </div>

              {/* Operator perspective */}
              <div className="mt-10">
                <h2 className="text-[20px] sm:text-[22px] font-bold text-[#111827] tracking-tight" style={{ letterSpacing: "-0.02em" }}>
                  Where 589 Labs is different
                </h2>
                <p className="mt-4 text-[15.5px] text-[#374151] leading-[1.75]">
                  Most AI consultants sound like futurists. We sound operational. Our work
                  is grounded in industry-specific assessments, practical execution,
                  measurable KPIs, executive-level guidance, and ongoing CAIO leadership —
                  not slide decks and demos.
                </p>
                <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-[14.5px] text-[#111827]">
                  {[
                    "Industry-specific AI assessments",
                    "Practical, phased execution",
                    "KPIs tied directly to business outcomes",
                    "Executive-level guidance &amp; sponsorship",
                    "Ongoing fractional CAIO leadership",
                    "Approval gates &amp; governance built in",
                  ].map((it) => (
                    <li key={it} className="flex items-start gap-2.5">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#1a56db] shrink-0" />
                      <span dangerouslySetInnerHTML={{ __html: it }} />
                    </li>
                  ))}
                </ul>
              </div>
            </article>

            {/* RIGHT — CTA card */}
            <aside
              className="bg-white border border-[#e5e7eb] rounded-2xl p-7 shadow-[0_4px_24px_rgba(17,24,39,0.06)] lg:sticky lg:top-24"
              data-testid="faq-article-cta-card"
            >
              <Sparkles className="h-7 w-7 text-[#1a56db] mb-5" />
              <div className="text-[18px] font-bold text-[#111827] leading-tight" style={{ letterSpacing: "-0.02em" }}>
                Ready to translate this into an action plan?
              </div>
              <p className="mt-3 text-[14px] text-[#4b5563] leading-[1.6]">
                Start with a free Preliminary Assessment — 15 questions, ~20 minutes,
                a ranked list of the highest-ROI AI opportunities for your operating
                model.
              </p>
              <Button
                onClick={() => navigate("/contact?intent=preliminary")}
                className="mt-6 h-12 rounded-xl bg-[#1a56db] text-white text-[14.5px] font-semibold hover:bg-[#143faa] w-full shadow-none"
                data-testid="faq-article-prelim-cta"
              >
                Start Preliminary Assessment
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/contact?intent=book")}
                className="mt-3 h-12 rounded-xl border border-[#e5e7eb] bg-white text-[#111827] text-[14.5px] font-semibold hover:bg-[#f9fafb] w-full shadow-none"
                data-testid="faq-article-book-cta"
              >
                Book a Conversation
              </Button>
              <a
                href="mailto:ddacosta33@yahoo.com"
                className="mt-4 inline-flex items-center gap-2 text-[13px] text-[#1a56db] font-semibold hover:underline"
              >
                <Mail className="h-3.5 w-3.5" /> ddacosta33@yahoo.com
              </a>
            </aside>
          </div>
        </div>
      </section>

      {/* ── RELATED QUESTIONS ── */}
      <section
        className="bg-[#f9fafb] border-t border-[#e5e7eb] py-20 lg:py-24"
        data-testid="faq-article-related"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="mb-10">
            <div className="text-[13px] font-semibold tracking-[0.05em] text-[#1a56db] uppercase mb-3">
              Keep going
            </div>
            <h2
              className="text-[26px] lg:text-[32px] font-bold text-[#111827] leading-[1.2] tracking-tight"
              style={{ letterSpacing: "-0.5px" }}
            >
              Related questions executives also ask.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {related.map((r) => {
              const RIcon = ICON_MAP[r.icon] || HelpCircle;
              return (
                <Link
                  key={r.slug}
                  to={`/faq/${r.slug}`}
                  onClick={() => setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 50)}
                  className="group rounded-2xl border border-[#e5e7eb] bg-white p-7 hover:border-[#1a56db] hover:shadow-[0_8px_24px_rgba(26,86,219,0.10)] transition-all"
                  data-testid={`faq-related-${r.slug}`}
                >
                  <span className="h-10 w-10 rounded-xl bg-[#e8eefb] text-[#1a56db] flex items-center justify-center mb-5 group-hover:bg-[#1a56db] group-hover:text-white transition-colors">
                    <RIcon className="h-4.5 w-4.5" />
                  </span>
                  <div className="text-[16px] font-semibold text-[#111827] leading-[1.4]" style={{ letterSpacing: "-0.01em" }}>
                    {r.question}
                  </div>
                  <p className="mt-3 text-[13.5px] text-[#6b7280] leading-[1.6] line-clamp-3">
                    {r.short}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#1a56db] group-hover:gap-2.5 transition-all">
                    Read answer <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-10">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#1a56db] hover:underline"
              data-testid="faq-article-back-all"
            >
              <ArrowLeft className="h-4 w-4" /> Back to all FAQs
            </Link>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="bg-[#0f172a] py-20 lg:py-24" data-testid="faq-article-final-cta">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 text-center">
          <Sparkles className="mx-auto h-7 w-7 text-[#E8A020] mb-5" />
          <h2
            className="text-[28px] lg:text-[36px] font-bold text-white leading-[1.15] tracking-tight max-w-3xl mx-auto"
            style={{ letterSpacing: "-0.5px" }}
          >
            See where AI fits in your operating model — in 20 minutes.
          </h2>
          <p className="mt-5 text-[15.5px] text-[#cbd5e1] leading-[1.7] max-w-2xl mx-auto">
            The Preliminary Assessment ranks your top AI opportunities, surfaces a
            funding-readiness snapshot, and recommends a clear next step.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Button
              onClick={() => navigate("/contact?intent=preliminary")}
              className="h-14 rounded-xl bg-[#1a56db] px-7 text-white text-[15px] font-semibold hover:bg-[#143faa] shadow-none"
            >
              Start Preliminary Assessment
              <ArrowRight className="ml-2 h-[18px] w-[18px]" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/contact?intent=book")}
              className="h-14 rounded-xl border border-white/20 bg-transparent text-white text-[15px] font-semibold hover:bg-white/10 px-6"
            >
              Book a Conversation
            </Button>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default FAQArticlePage;
